import React, { useState, useEffect, useContext } from "react";
import { RouteComponentProps } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Caret } from "@cmsgov/ds-medicare-gov";
import { Plan } from "../../@types";
import { getPlan } from "../../api";
import {
  PrintButton,
  AppPage,
  ReferrerContext,
  EnrollmentDownAlert,
} from "../../components";
import {
  parsePlanLongIdIntoUrlPartForApi,
  isMedicareAdvantageType,
  logError,
  makePlanLongId,
  sendTealiumEvent,
} from "../../helpers";
import { MAPDPlanDetails, PDPPlanDetails, PlanDetailsErrorPage } from "./";
import { FormattedMessage } from "react-intl-phraseapp";
import routes from "../../app/routes";
import { AppStore } from "../../app/store";
import URI from "urijs";

export interface PlanDetailsState {
  plan?: Plan;
  error?: Error;
}

interface PlanDetailsMatchParams {
  id: string;
}

const PlanDetailsPage: React.FunctionComponent<RouteComponentProps> = ({
  location,
  match,
}) => {
  const [error, setError] = useState(false);
  const [plan, setPlan] = useState<undefined | Plan>(undefined);
  const [fetchingPlan, setFetchingPlan] = useState(false);
  const {
    state: { lis, compareList, year, futureLis },
  } = useContext(AppStore.AppContext);
  const { mctCurrentPlanYear = "" } = useFlags();

  const lisToSend =
    year !== mctCurrentPlanYear.toString() && futureLis ? futureLis : lis;

  const fetchPlan = async (planId: string): Promise<void> => {
    setFetchingPlan(true);
    try {
      const fetchedPlan = await getPlan(
        planId,
        lisToSend ? { lis: lisToSend } : {}
      );
      setPlan(fetchedPlan);
      setError(false);
      setFetchingPlan(false);
    } catch (err) {
      setError(true);
      setFetchingPlan(false);
      logError("Failed to retrieve plan (PlanDetailsPage)", err);
    }
  };

  const goBackUrl = (goBackTextId: string): string => {
    if (
      goBackTextId === "app.terms.go_back_to_plan_compare" &&
      compareList.length
    ) {
      const compareURI = new URI(routes.comparePlans);
      compareURI.addSearch(
        "plans",
        compareList.map(plan => makePlanLongId(plan))
      );

      sendTealiumEvent("Plan Details", "Back to Plan Compare");
      return compareURI.toString();
    } else {
      sendTealiumEvent("Plan Details", "Back to Search Results");
      return `${routes.searchResults}${location.search}`;
    }
  };

  let DetailsPage: React.ReactNode;

  if (plan) {
    DetailsPage = isMedicareAdvantageType(plan.plan_type) ? (
      <MAPDPlanDetails plan={plan} />
    ) : (
      <PDPPlanDetails plan={plan} />
    );
  } else {
    DetailsPage = <FormattedMessage tagName="h1" id={"app.loading"} />;
  }

  useEffect(() => {
    const planId = parsePlanLongIdIntoUrlPartForApi(
      (match.params as PlanDetailsMatchParams).id
    );
    if (planId && !fetchingPlan) {
      fetchPlan(planId);
    }
  }, []);

  return !error ? (
    <ReferrerContext.Consumer>
      {(goBackTextId): JSX.Element => (
        <AppPage
          subHeading={
            <>
              <EnrollmentDownAlert
                className="ds-u-margin-bottom--3"
                plan={plan}
              />
              {error ? null : (
                <div className="ds-l-row ds-u-justify-content--between mct-hide-print">
                  <HashLink
                    to={goBackUrl(goBackTextId)}
                    className="ds-c-button ds-c-button--transparent PlanDetails__backButton"
                  >
                    <Caret
                      left
                      className="ds-u-valign--middle ds-u-margin-right--1"
                    />
                    {goBackTextId && <FormattedMessage id={goBackTextId} />}
                  </HashLink>

                  <PrintButton
                    tealiumProps={{
                      event_action: "Plan Details",
                      event_label: "Print",
                    }}
                  />
                </div>
              )}
            </>
          }
        >
          <div className="e2e-plan-details-page">{DetailsPage}</div>
        </AppPage>
      )}
    </ReferrerContext.Consumer>
  ) : (
    <PlanDetailsErrorPage />
  );
};

export default PlanDetailsPage;
